import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServices } from '@/contexts/ServicesContext.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const { sessionService } = useServices();
  const addTab = useChatTabsStore((state) => state.addTab);
  const closeTab = useChatTabsStore((state) => state.closeTab);
  const activeTabId = useChatTabsStore((state) => state.activeTabId);
  const tabs = useChatTabsStore((state) => state.tabs);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      const isInputting =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Alt + N: New chat (navigate to welcome without creating a new tab)
      if (e.altKey && e.key === 'n' && !isInputting) {
        e.preventDefault();
        sessionService.returnToWelcome();
        navigate('/');
        return;
      }

      // Alt + T: New chat tab
      if (e.altKey && e.key === 't' && !isInputting) {
        e.preventDefault();
        const uniqueId = `welcome-${Date.now()}`;
        addTab({
          id: uniqueId,
          title: 'New Chat',
          sessionId: null,
          isPinned: false,
        });
        sessionService.returnToWelcome();
        navigate('/');
        return;
      }

      // Alt + W: Close current tab
      if (e.altKey && e.key === 'w' && !isInputting) {
        e.preventDefault();

        // Don't allow closing if it's the last tab
        if (tabs.length <= 1) {
          return;
        }

        const currentTab = tabs.find((t) => t.id === activeTabId);
        if (currentTab) {
          closeTab(activeTabId);

          // Navigate to the new active tab
          const remainingTabs = tabs.filter((t) => t.id !== activeTabId);
          if (remainingTabs.length > 0) {
            const newActiveTab = remainingTabs[0];
            if (newActiveTab.sessionId) {
              navigate(`/chat/${newActiveTab.sessionId}`);
            } else {
              sessionService.returnToWelcome();
              navigate('/');
            }
          }
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, sessionService, addTab, closeTab, activeTabId, tabs]);
}
